<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { format } from 'date-fns'
  import Button from '@/components/ui/button/Button.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import { Loader2, Sparkles } from 'lucide-vue-next'
  import { useToast } from '@/components/ui/toast/use-toast'
  import { usePlaceholders } from '@/composables/usePlaceholders'
  import { ROUTES } from '@/router/routes'
  import { Separator } from '@/components/ui/separator'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: {
      labelsGenerated: 'Labels generated',
      labelsGeneratedDesc: 'AI has suggested labels for your placeholder',
      generationFailed: 'Generation failed',
      generationFailedDesc: 'Failed to generate labels with AI',
      placeholderUpdated: 'Placeholder updated',
      changesSavedSuccess: 'Changes have been saved successfully',
      error: 'Error',
      updateFailed: 'Failed to update placeholder',
      saveChanges: 'Save Changes',
      dashboard: 'Dashboard',
      placeholders: 'Placeholders',
      editPlaceholder: 'Edit Placeholder',
      placeholderName: 'Placeholder Name',
      placeholderNamePlaceholder: 'e.g., client_first_name',
      placeholderNameDesc: 'The technical name used in templates. Cannot be changed.',
      germanLabel: 'German Label',
      germanLabelPlaceholder: 'e.g., Vorname des Kunden',
      germanLabelDesc: 'Human-readable label in German',
      englishLabel: 'English Label',
      englishLabelPlaceholder: 'e.g., Client First Name',
      englishLabelDesc: 'Human-readable label in English',
      generateLabelsWithAI: 'Generate Labels with AI',
      aiSuggestLabels: 'AI will suggest labels based on the placeholder name',
      usageInformation: 'Usage Information',
      templatesUsingPlaceholder: 'Templates using this placeholder',
      created: 'Created',
      lastUpdated: 'Last Updated',
      na: 'N/A'
    },
    de: {
      labelsGenerated: 'Labels generiert',
      labelsGeneratedDesc: 'KI hat Labels für Ihren Platzhalter vorgeschlagen',
      generationFailed: 'Generierung fehlgeschlagen',
      generationFailedDesc: 'Labels konnten nicht mit KI generiert werden',
      placeholderUpdated: 'Platzhalter aktualisiert',
      changesSavedSuccess: 'Änderungen wurden erfolgreich gespeichert',
      error: 'Fehler',
      updateFailed: 'Platzhalter konnte nicht aktualisiert werden',
      saveChanges: 'Änderungen speichern',
      dashboard: 'Dashboard',
      placeholders: 'Platzhalter',
      editPlaceholder: 'Platzhalter bearbeiten',
      placeholderName: 'Platzhaltername',
      placeholderNamePlaceholder: 'z.B. client_first_name',
      placeholderNameDesc:
        'Der technische Name, der in Vorlagen verwendet wird. Kann nicht geändert werden.',
      germanLabel: 'Deutsches Label',
      germanLabelPlaceholder: 'z.B. Vorname des Kunden',
      germanLabelDesc: 'Lesbares Label auf Deutsch',
      englishLabel: 'Englisches Label',
      englishLabelPlaceholder: 'z.B. Client First Name',
      englishLabelDesc: 'Lesbares Label auf Englisch',
      generateLabelsWithAI: 'Labels mit KI generieren',
      aiSuggestLabels: 'KI schlägt Labels basierend auf dem Platzhalternamen vor',
      usageInformation: 'Nutzungsinformationen',
      templatesUsingPlaceholder: 'Vorlagen, die diesen Platzhalter verwenden',
      created: 'Erstellt',
      lastUpdated: 'Zuletzt aktualisiert',
      na: 'N/A'
    }
  })

  const router = useRouter()
  const route = useRoute()
  const { toast } = useToast()
  const {
    placeholder,
    isLoading,
    fetchPlaceholder,
    updatePlaceholder,
    generateLabelsForPlaceholder
  } = usePlaceholders()

  const isSaving = ref(false)
  const isGenerating = ref(false)

  const form = ref({
    placeholder_name: '',
    german_label: '',
    english_label: ''
  })

  const originalForm = ref({
    placeholder_name: '',
    german_label: '',
    english_label: ''
  })

  const isFormValid = computed(() => {
    return form.value.placeholder_name.trim() !== ''
  })

  const hasChanges = computed(() => {
    return (
      form.value.german_label !== originalForm.value.german_label ||
      form.value.english_label !== originalForm.value.english_label
    )
  })

  const formatDate = (date: string) => {
    return format(new Date(date), 'MMM d, yyyy HH:mm')
  }

  const navigateBack = () => {
    router.push(ROUTES.ADMIN.WORD_TEMPLATES.PLACEHOLDERS)
  }

  const generateLabels = async () => {
    if (!form.value.placeholder_name) return

    isGenerating.value = true
    try {
      const labels = await generateLabelsForPlaceholder(form.value.placeholder_name)
      if (labels) {
        form.value.german_label = labels.german_label || form.value.german_label
        form.value.english_label = labels.english_label || form.value.english_label
        toast({
          title: t('labelsGenerated'),
          description: t('labelsGeneratedDesc')
        })
      }
    } catch (error) {
      toast({
        title: t('generationFailed'),
        description: t('generationFailedDesc'),
        variant: 'destructive'
      })
    } finally {
      isGenerating.value = false
    }
  }

  const savePlaceholder = async () => {
    if (!isFormValid.value) return

    isSaving.value = true
    try {
      await updatePlaceholder(route.params.uuid as string, form.value)
      originalForm.value = { ...form.value }
      toast({
        title: t('placeholderUpdated'),
        description: t('changesSavedSuccess')
      })
    } catch (error) {
      toast({
        title: t('error'),
        description: t('updateFailed'),
        variant: 'destructive'
      })
    } finally {
      isSaving.value = false
    }
  }

  onMounted(async () => {
    await fetchPlaceholder(route.params.uuid as string)
    if (placeholder.value) {
      const formData = {
        placeholder_name: placeholder.value.placeholder_name,
        german_label: placeholder.value.german_label || '',
        english_label: placeholder.value.english_label || ''
      }
      form.value = { ...formData }
      originalForm.value = { ...formData }
    }
  })
</script>

<template>
  <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
    <div class="flex items-center justify-between gap-x-5">
      <Breadcrumbs
        :items="[
          { title: t('dashboard'), to: ROUTES.ADMIN.DASHBOARD },
          { title: t('placeholders'), to: ROUTES.ADMIN.WORD_TEMPLATES.PLACEHOLDERS },
          {
            title: placeholder?.placeholder_name || t('editPlaceholder'),
            to: ROUTES.ADMIN.WORD_TEMPLATES.PLACEHOLDER_DETAIL.replace(
              ':uuid',
              route.params.uuid as string
            )
          }
        ]"
      />

      <div class="flex items-center justify-end gap-x-3">
        <Button
          variant="default"
          class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-4 py-2
            text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
            focus:ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
          :disabled="isSaving || !isFormValid || !hasChanges"
          @click="savePlaceholder"
        >
          <Loader2
            v-if="isSaving"
            class="mr-2 h-4 w-4 animate-spin"
          />
          {{ t('saveChanges') }}
        </Button>
      </div>
    </div>

    <div
      v-if="isLoading"
      class="rounded-xl border border-gray-200 bg-white p-8 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
    >
      <div class="flex items-center justify-center">
        <Loader2 class="h-8 w-8 animate-spin" />
      </div>
    </div>

    <div
      v-else
      class="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
    >
      <form
        class="space-y-6"
        @submit.prevent="savePlaceholder"
      >
        <div class="space-y-4">
          <div>
            <InputField
              v-model="form.placeholder_name"
              name="placeholder_name"
              :label="t('placeholderName')"
              :placeholder="t('placeholderNamePlaceholder')"
              :disabled="true"
              class="font-mono"
            />
            <p class="mt-1 text-sm text-muted-foreground">
              {{ t('placeholderNameDesc') }}
            </p>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <InputField
                v-model="form.german_label"
                name="german_label"
                :label="t('germanLabel')"
                :placeholder="t('germanLabelPlaceholder')"
              />
              <p class="mt-1 text-sm text-muted-foreground">{{ t('germanLabelDesc') }}</p>
            </div>

            <div>
              <InputField
                v-model="form.english_label"
                name="english_label"
                :label="t('englishLabel')"
                :placeholder="t('englishLabelPlaceholder')"
              />
              <p class="mt-1 text-sm text-muted-foreground">{{ t('englishLabelDesc') }}</p>
            </div>
          </div>

          <div class="flex items-center gap-4">
            <Button
              type="button"
              variant="outline"
              class="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-black shadow-sm
                hover:bg-gray-100 hover:text-gray-900"
              :disabled="!form.placeholder_name || isGenerating"
              @click="generateLabels"
            >
              <Sparkles
                v-if="!isGenerating"
                class="mr-2 h-4 w-4"
              />
              <Loader2
                v-else
                class="mr-2 h-4 w-4 animate-spin"
              />
              {{ t('generateLabelsWithAI') }}
            </Button>
            <span class="text-sm text-muted-foreground">
              {{ t('aiSuggestLabels') }}
            </span>
          </div>
        </div>

        <Separator />

        <div class="space-y-4">
          <h3 class="text-lg font-semibold">{{ t('usageInformation') }}</h3>

          <div class="grid gap-4 md:grid-cols-3">
            <div
              class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
            >
              <div class="text-2xl font-bold">{{ placeholder?.template_count ?? 0 }}</div>
              <div class="text-sm text-muted-foreground">{{ t('templatesUsingPlaceholder') }}</div>
            </div>

            <div
              class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
            >
              <div class="text-sm font-medium">{{ t('created') }}</div>
              <div class="text-sm text-muted-foreground">
                {{ placeholder?.created_at ? formatDate(placeholder.created_at) : t('na') }}
              </div>
            </div>

            <div
              class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
            >
              <div class="text-sm font-medium">{{ t('lastUpdated') }}</div>
              <div class="text-sm text-muted-foreground">
                {{ placeholder?.updated_at ? formatDate(placeholder.updated_at) : t('na') }}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
